import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShoppingBag, Globe, Plus, X, ArrowRight, Trash2 } from 'lucide-react';
import { getStorePlatformLabel, isPlatformManagedStore, useConsoleStore, type ConsoleStore } from '../store';
import { ApiError, apiFetch } from '../lib/api';
import { cn } from '../lib/utils';

type AddSiteResponse =
  | {
      status: 'pending';
      domain: string;
      expiresAt: string;
      snippet: string;
    }
  | {
      status: 'verified';
      store: ConsoleStore;
      snippet: string;
    };

type PendingVerification = {
  id: string;
  domain: string;
  expiresAt: string | null;
  snippet: string;
};

type PendingVerificationsResponse = {
  verifications: PendingVerification[];
};

type LegalRetryAction =
  | { type: 'link'; domain: string }
  | { type: 'check'; domain: string; displayErrorInModal: boolean }
  | { type: 'regenerate'; domain: string }
  | { type: 'viewTag'; store: ConsoleStore };

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  past_due: 'bg-amber-50 text-amber-700 border-amber-100',
  cancelled: 'bg-zinc-50 text-zinc-500 border-zinc-100',
};
const VERIFICATION_AUTO_POLL_MS = 4_000;
const VERIFICATION_AUTO_POLL_MAX_MS = 5 * 60 * 1000;

function StoreCard({
  store,
  onClick,
  onRemove,
  onViewTag,
}: {
  store: ConsoleStore;
  onClick: () => void;
  onRemove: () => void;
  onViewTag?: () => void;
}) {
  const Icon = store.platform === 'shopify' ? ShoppingBag : Globe;
  const statusColor = STATUS_COLORS[store.subscriptionStatus] ?? STATUS_COLORS.cancelled;
  const platformLabel = getStorePlatformLabel(store);
  const storeMode = isPlatformManagedStore(store) ? 'Platform connected' : 'Standard site';
  
  return (
    <div className="group flex flex-col gap-4 p-6 rounded-2xl border border-zinc-200/60 bg-white hover:border-cyan-200 hover:shadow-[0_8px_30px_rgba(8,145,178,0.12)] transition-all text-left relative overflow-hidden">
      <div className="flex items-start justify-between relative z-10">
        <div className="w-10 h-10 rounded-xl bg-zinc-50 flex items-center justify-center border border-zinc-100 group-hover:bg-cyan-50 group-hover:border-cyan-100 transition-colors">
          <Icon size={18} className="text-zinc-600 group-hover:text-cyan-700 transition-colors" />
        </div>
        <span className={cn('text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-lg border', statusColor)}>
          {store.subscriptionStatus}
        </span>
      </div>
      
      <div className="relative z-10">
        <p className="font-bold text-zinc-900 text-base truncate tracking-tight group-hover:text-slate-950 transition-colors">{store.externalDomain}</p>
        <p className="text-xs font-medium text-zinc-400 capitalize mt-1 flex items-center gap-1.5">
          {platformLabel} <span className="w-1 h-1 rounded-full bg-zinc-200" /> {store.planTier} plan
        </p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-sky-700">{storeMode}</p>
          {onViewTag && (
            <button
              type="button"
              onClick={onViewTag}
              className="rounded-lg border border-zinc-200 px-2 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500 transition-colors hover:border-cyan-200 hover:bg-cyan-50 hover:text-cyan-700"
            >
              Install Tag
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-1 relative z-10">
        <button
          type="button"
          onClick={onClick}
          className="flex items-center gap-1.5 text-xs font-bold text-zinc-400 group-hover:text-cyan-700 transition-colors"
        >
          Open appearance <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-bold text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 size={13} /> Remove
        </button>
      </div>

      {/* Subtle background glow on hover */}
      <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-cyan-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity blur-2xl" />
    </div>
  );
}

function PendingVerificationCard({
  verification,
  checking,
  error,
  onCheck,
  onRegenerate,
}: {
  verification: PendingVerification;
  checking: boolean;
  error?: string;
  onCheck: () => void;
  onRegenerate: () => void;
}) {
  return (
    <div className="rounded-3xl border border-cyan-100 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-700">Pending site verification</p>
          <h2 className="mt-2 truncate text-xl font-extrabold tracking-tight text-slate-950">{verification.domain}</h2>
          <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-slate-500">
            Keep the install tag live on your site. We auto-check verification in the background and you can also use <span className="font-bold">Check now</span>.
          </p>
          {verification.expiresAt && (
            <p className="mt-3 text-xs font-bold text-slate-400">
              Verification window expires {new Date(verification.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.
            </p>
          )}
          {error && (
            <p className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{error}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
          <button
            type="button"
            onClick={onCheck}
            disabled={checking}
            className="rounded-xl bg-[#0f3d5e] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#124a66] disabled:opacity-50"
          >
            {checking ? 'Checking...' : 'Check now'}
          </button>
          <button
            type="button"
            onClick={onRegenerate}
            className="rounded-xl border border-cyan-100 bg-cyan-50 px-4 py-2.5 text-sm font-bold text-cyan-800 transition-all hover:bg-cyan-100"
          >
            Generate fresh tag
          </button>
        </div>
      </div>
    </div>
  );
}

export function StoreSelector() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { stores, setActiveStoreId, setStores } = useConsoleStore();
  const [showModal, setShowModal] = useState(false);
  const [domain, setDomain] = useState('');
  const [verification, setVerification] = useState<Extract<AddSiteResponse, { status: 'pending' }> | null>(null);
  const [snippet, setSnippet] = useState('');
  const [storeToRemove, setStoreToRemove] = useState<ConsoleStore | null>(null);
  const [deleteConfirmDomain, setDeleteConfirmDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removalMode, setRemovalMode] = useState<'unlink' | 'delete'>('unlink');
  const [checkingDomain, setCheckingDomain] = useState('');
  const [pendingErrors, setPendingErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [removeError, setRemoveError] = useState('');
  const [copiedSnippet, setCopiedSnippet] = useState<boolean>(false);
  const [copyError, setCopyError] = useState('');
  const [verificationCompleteDomain, setVerificationCompleteDomain] = useState<string | null>(null);
  const [legalConsentOpen, setLegalConsentOpen] = useState(false);
  const [legalConsentSubmitting, setLegalConsentSubmitting] = useState(false);
  const [legalConsentError, setLegalConsentError] = useState('');
  const [legalRetryAction, setLegalRetryAction] = useState<LegalRetryAction | null>(null);
  const verificationCheckInFlightRef = useRef(false);

  const THEME_COLOR = 'var(--action-primary)';
  const legalSiteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://veahub.com';

  const { data: pendingVerificationsData } = useQuery({
    queryKey: ['site-verifications'],
    queryFn: () => apiFetch<PendingVerificationsResponse>('/api/console/site-verifications'),
  });
  const pendingVerifications = pendingVerificationsData?.verifications ?? [];

  const handleStoreClick = (store: ConsoleStore) => {
    setActiveStoreId(store.id);
    navigate('/appearance');
  };

  const openRemoveModal = (store: ConsoleStore) => {
    setStoreToRemove(store);
    setRemovalMode('unlink');
    setDeleteConfirmDomain('');
    setRemoveError('');
  };

  const isLegalConsentRequired = (err: unknown) => {
    return err instanceof ApiError && err.code === 'legal_consent_required';
  };

  const promptLegalConsent = (action: LegalRetryAction) => {
    setLegalRetryAction(action);
    setLegalConsentError('');
    setLegalConsentOpen(true);
  };

  const getLegalConsentPayload = (action: LegalRetryAction | null) => {
    if (!action) return {};
    if (action.type === 'viewTag') {
      return {
        storeId: action.store.id,
        storeDomain: action.store.externalDomain,
      };
    }
    return { storeDomain: action.domain };
  };

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch<AddSiteResponse>('/api/console/stores/link', { method: 'POST', body: JSON.stringify({ domain }) });
      if (result.status === 'pending') {
        setVerification(result);
        setVerificationCompleteDomain(null);
        setSnippet(result.snippet);
        await queryClient.invalidateQueries({ queryKey: ['site-verifications'] });
        return;
      }
      const linkedStore = result.store;
      const nextStores = stores.some((store) => store.id === linkedStore.id)
        ? stores.map((store) => store.id === linkedStore.id ? linkedStore : store)
        : [...stores, linkedStore];
      setStores(nextStores);
      setActiveStoreId(linkedStore.id);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setVerification(null);
      setVerificationCompleteDomain(linkedStore.externalDomain);
      setSnippet(result.snippet);
      setShowModal(true);
      setDomain(linkedStore.externalDomain);
    } catch (err: any) {
      if (isLegalConsentRequired(err)) {
        promptLegalConsent({ type: 'link', domain });
        return;
      }
      setError(err.message || 'Failed to link store');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSuccess = async (linkedStore: ConsoleStore, snippetValue: string) => {
    const nextStores = stores.some((store) => store.id === linkedStore.id)
      ? stores.map((store) => store.id === linkedStore.id ? linkedStore : store)
      : [...stores, linkedStore];
    setStores(nextStores);
    setActiveStoreId(linkedStore.id);
    setSnippet(snippetValue);
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    await queryClient.invalidateQueries({ queryKey: ['site-verifications'] });
    setVerification(null);
    setVerificationCompleteDomain(linkedStore.externalDomain);
    setShowModal(true);
    setDomain(linkedStore.externalDomain);
  };

  const handleCheckVerification = async (
    domainToCheck = verification?.domain,
    displayErrorInModal = true,
    silentPending = false,
  ): Promise<boolean> => {
    if (!domainToCheck) return false;
    if (verificationCheckInFlightRef.current) return false;
    verificationCheckInFlightRef.current = true;
    setChecking(true);
    setCheckingDomain(domainToCheck);
    setError('');
    if (!silentPending) {
      setPendingErrors((current) => ({ ...current, [domainToCheck]: '' }));
    }
    try {
      const result = await apiFetch<AddSiteResponse | { status: 'not_started'; snippet?: string; expiresAt?: string | null }>(
        `/api/console/stores/verification-status?domain=${encodeURIComponent(domainToCheck)}`
      );
      if (result.status !== 'verified') {
        if (!silentPending) {
          const message = 'Not verified yet. Keep the install tag live, then try again.';
          if (displayErrorInModal) setError(message);
          setPendingErrors((current) => ({ ...current, [domainToCheck]: message }));
        }
        return false;
      }
      await handleVerificationSuccess(result.store, result.snippet);
      return true;
    } catch (err: any) {
      if (isLegalConsentRequired(err)) {
        promptLegalConsent({ type: 'check', domain: domainToCheck, displayErrorInModal });
        return false;
      }
      const message = err.message || 'Failed to check verification';
      if (displayErrorInModal) setError(message);
      setPendingErrors((current) => ({ ...current, [domainToCheck]: message }));
      return false;
    } finally {
      verificationCheckInFlightRef.current = false;
      setChecking(false);
      setCheckingDomain('');
    }
  };

  useEffect(() => {
    if (!verification?.domain) return;

    let elapsedMs = 0;
    const domainToCheck = verification.domain;
    setPendingErrors((current) => ({ ...current, [domainToCheck]: '' }));

    const pollVerification = async () => {
      if (document.hidden) return;
      const verified = await handleCheckVerification(domainToCheck, false, true);
      if (verified) return;

      elapsedMs += VERIFICATION_AUTO_POLL_MS;
      if (elapsedMs >= VERIFICATION_AUTO_POLL_MAX_MS) {
        setPendingErrors((current) => ({
          ...current,
          [domainToCheck]: 'Still waiting for verification. Keep the install tag on your site and use Check now when ready.',
        }));
        clearInterval(intervalId);
      }
    };

    const intervalId = window.setInterval(() => {
      void pollVerification();
    }, VERIFICATION_AUTO_POLL_MS);
    void pollVerification();

    return () => {
      clearInterval(intervalId);
    };
  }, [verification?.domain]);

  const handleRegenerateVerification = async (domainToRegenerate: string) => {
    setLoading(true);
    setError('');
    setPendingErrors((current) => ({ ...current, [domainToRegenerate]: '' }));
    try {
      const result = await apiFetch<AddSiteResponse>('/api/console/stores/link', {
        method: 'POST',
        body: JSON.stringify({ domain: domainToRegenerate }),
      });
      if (result.status === 'pending') {
        setDomain(domainToRegenerate);
        setVerification(result);
        setVerificationCompleteDomain(null);
        setSnippet(result.snippet);
        setShowModal(true);
        await queryClient.invalidateQueries({ queryKey: ['site-verifications'] });
        return;
      }
      await handleVerificationSuccess(result.store, result.snippet);
    } catch (err: any) {
      if (isLegalConsentRequired(err)) {
        promptLegalConsent({ type: 'regenerate', domain: domainToRegenerate });
        return;
      }
      setPendingErrors((current) => ({ ...current, [domainToRegenerate]: err.message || 'Failed to generate a fresh tag' }));
    } finally {
      setLoading(false);
    }
  };

  const resetAddSiteModal = () => {
    setShowModal(false);
    setVerification(null);
    setSnippet('');
    setDomain('');
    setError('');
    setCopyError('');
    setCopiedSnippet(false);
    setVerificationCompleteDomain(null);
  };

  const handleViewInstallTag = async (store: ConsoleStore) => {
    setLoading(true);
    setError('');
    setCopyError('');
    try {
      const result = await apiFetch<AddSiteResponse>('/api/console/stores/link', {
        method: 'POST',
        body: JSON.stringify({ domain: store.externalDomain }),
      });
      if (result.status !== 'verified') {
        setError('Generate an install tag first for this domain.');
        return;
      }
      setDomain(store.externalDomain);
      setVerification(null);
      setSnippet(result.snippet);
      setVerificationCompleteDomain(store.externalDomain);
      setShowModal(true);
    } catch (err: any) {
      if (isLegalConsentRequired(err)) {
        promptLegalConsent({ type: 'viewTag', store });
        return;
      }
      setError(err.message || 'Unable to load install tag');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyError('');
      setCopiedSnippet(true);
      setTimeout(() => {
        setCopiedSnippet(false);
      }, 2000);
    } catch {
      setCopiedSnippet(false);
      setCopyError('Copy failed in this browser context. Copy manually from the snippet block.');
    }
  };

  const handleRemoveStore = async () => {
    if (!storeToRemove) return;
    setRemoving(true);
    setRemoveError('');
    try {
      await apiFetch(`/api/console/stores/${encodeURIComponent(storeToRemove.id)}`, {
        method: 'DELETE',
        body: JSON.stringify({
          mode: removalMode,
          confirmDomain: removalMode === 'delete' ? deleteConfirmDomain : undefined,
        }),
      });
      const nextStores = stores.filter((store) => store.id !== storeToRemove.id);
      setStores(nextStores);
      setActiveStoreId(nextStores[0]?.id ?? null);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await queryClient.invalidateQueries({ queryKey: ['site-verifications'] });
      setStoreToRemove(null);
      setDeleteConfirmDomain('');
      setRemovalMode('unlink');
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove store');
    } finally {
      setRemoving(false);
    }
  };

  const retryLegalBlockedAction = async (action: LegalRetryAction) => {
    if (action.type === 'link') {
      setDomain(action.domain);
      const result = await apiFetch<AddSiteResponse>('/api/console/stores/link', {
        method: 'POST',
        body: JSON.stringify({ domain: action.domain }),
      });
      if (result.status === 'pending') {
        setVerification(result);
        setVerificationCompleteDomain(null);
        setSnippet(result.snippet);
        setShowModal(true);
        await queryClient.invalidateQueries({ queryKey: ['site-verifications'] });
        return;
      }
      await handleVerificationSuccess(result.store, result.snippet);
      return;
    }

    if (action.type === 'check') {
      await handleCheckVerification(action.domain, action.displayErrorInModal);
      return;
    }

    if (action.type === 'regenerate') {
      await handleRegenerateVerification(action.domain);
      return;
    }

    await handleViewInstallTag(action.store);
  };

  const handleLegalConsentAgree = async () => {
    setLegalConsentSubmitting(true);
    setLegalConsentError('');
    try {
      const action = legalRetryAction;
      await apiFetch('/api/console/legal/consent', {
        method: 'POST',
        body: JSON.stringify(getLegalConsentPayload(action)),
      });
      setLegalConsentOpen(false);
      setLegalRetryAction(null);
      await queryClient.invalidateQueries({ queryKey: ['site-verifications'] });
      if (action) await retryLegalBlockedAction(action);
    } catch (err: any) {
      setLegalConsentError(err.message || 'Failed to record legal consent.');
    } finally {
      setLegalConsentSubmitting(false);
    }
  };

  return (
    <div className="p-10 max-w-5xl mx-auto">
      <header className="flex flex-col gap-1 mb-10">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-extrabold text-zinc-900 tracking-tight">Your stores</h1>
          <button
            onClick={() => {
              setVerification(null);
              setVerificationCompleteDomain(null);
              setSnippet('');
              setDomain('');
              setError('');
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold hover:brightness-110 transition-all shadow-sm active:scale-[0.98]"
            style={{ backgroundColor: THEME_COLOR }}
          >
            <Plus size={16} strokeWidth={3} />
            Add site
          </button>
        </div>
        <p className="text-zinc-500 text-sm font-medium">Manage platform stores and standard websites from one console.</p>
      </header>

      {pendingVerifications.length > 0 && (
        <section className="mb-8 space-y-3">
          {pendingVerifications.map((pendingVerification) => (
            <PendingVerificationCard
              key={pendingVerification.id}
              verification={pendingVerification}
              checking={checking && checkingDomain === pendingVerification.domain}
              error={pendingErrors[pendingVerification.domain]}
              onCheck={() => handleCheckVerification(pendingVerification.domain, false)}
              onRegenerate={() => handleRegenerateVerification(pendingVerification.domain)}
            />
          ))}
        </section>
      )}

      {stores.length === 0 && pendingVerifications.length === 0 ? (
        <div className="bg-white border border-zinc-200/60 rounded-3xl p-16 text-center shadow-sm">
          <div className="w-16 h-16 bg-zinc-50 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-zinc-100">
            <Globe size={32} className="text-zinc-400" />
          </div>
          <h2 className="text-xl font-bold text-zinc-900 mb-2 tracking-tight">Connect your first store</h2>
          <p className="text-sm text-zinc-500 mb-8 max-w-sm mx-auto leading-relaxed">
            Add a standard website now, then configure its widget, assistant, analytics, and subscription from the console workspace.
          </p>
          <button
            onClick={() => {
              setVerification(null);
              setVerificationCompleteDomain(null);
              setSnippet('');
              setDomain('');
              setError('');
              setShowModal(true);
            }}
            className="px-6 py-3 rounded-xl text-white text-sm font-bold hover:brightness-110 transition-all shadow-md active:scale-[0.98]"
            style={{ backgroundColor: THEME_COLOR }}
          >
            Add standard site
          </button>
        </div>
      ) : stores.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {stores.map(store => (
            <StoreCard
              key={store.id}
              store={store}
              onClick={() => handleStoreClick(store)}
              onRemove={() => openRemoveModal(store)}
              onViewTag={!isPlatformManagedStore(store) ? () => handleViewInstallTag(store) : undefined}
            />
          ))}
        </div>
      ) : null}

      {showModal && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-6 animate-in fade-in duration-200">
          <div
            className={cn(
              'bg-white rounded-[2rem] border border-zinc-200 shadow-2xl w-full p-8 animate-in zoom-in-95 duration-200',
              (verification || verificationCompleteDomain) ? 'max-w-2xl' : 'max-w-md'
            )}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-zinc-900 tracking-tight">Add a standard site</h2>
                <p className="text-sm text-zinc-500 font-medium mt-1">Use this for websites that are not managed through Shopify or WordPress.</p>
              </div>
              <button onClick={resetAddSiteModal} className="w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors">
                <X size={20} />
              </button>
            </div>
            
            {verification ? (
              <div className="space-y-5">
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-sky-100 bg-sky-50 p-4 md:col-span-2">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-sky-700">Install checklist</p>
                    <ol className="mt-3 space-y-2 text-sm font-medium leading-6 text-slate-700">
                      <li>1. Open <code className="rounded bg-white/90 px-1 py-0.5 text-xs font-bold text-slate-700">apps/site/index.html</code>.</li>
                      <li>2. Paste this install script right before <code className="rounded bg-white/90 px-1 py-0.5 text-xs font-bold text-slate-700">&lt;/head&gt;</code>.</li>
                      <li>3. Deploy and refresh the site tab, then confirm this tag appears in page source (<code className="rounded bg-white/90 px-1 py-0.5 text-xs font-bold text-slate-700">view-source:</code>).</li>
                      <li>4. Keep this same tag in place after verification. No second tag swap is required.</li>
                    </ol>
                  </div>
                  <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-600">Target domain</p>
                    <p className="mt-2 text-sm font-extrabold break-all text-zinc-900">{verification.domain}</p>
                    <p className="mt-2 text-xs font-semibold leading-5 text-zinc-500">Keep this exact domain value in the script tag.</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-zinc-200 bg-zinc-950 p-4 text-left">
                  <p className="mb-3 text-[11px] font-black uppercase tracking-[0.16em] text-zinc-400">Install script</p>
                  <code className="block whitespace-pre-wrap break-all text-xs leading-5 text-zinc-100">{verification.snippet}</code>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(verification.snippet)}
                  className={cn(
                    'w-full rounded-xl border py-3 text-sm font-bold transition-colors',
                    copiedSnippet
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                  )}
                >
                  {copiedSnippet ? 'Install tag copied' : 'Copy install tag'}
                </button>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-xs font-medium text-red-600">
                    {error}
                  </div>
                )}
                {copyError && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-xs font-medium text-amber-700">
                    {copyError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={resetAddSiteModal} className="flex-1 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors">Close</button>
                  <button
                    type="button"
                    onClick={() => handleCheckVerification()}
                    disabled={checking}
                    className="flex-1 py-3 rounded-xl text-white text-sm font-bold hover:brightness-110 disabled:opacity-50 transition-all shadow-sm"
                    style={{ backgroundColor: THEME_COLOR }}
                  >
                    {checking ? 'Checking…' : 'Check now'}
                  </button>
                </div>
              </div>
            ) : verificationCompleteDomain && !snippet ? (
              <div className="space-y-5">
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">Store linked</p>
                  <h3 className="mt-2 text-lg font-extrabold tracking-tight text-zinc-900 break-all">{verificationCompleteDomain}</h3>
                  <p className="mt-2 text-sm font-medium leading-6 text-emerald-900/80">
                    This store is ready in the console. No verification tag is needed for this connection.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={resetAddSiteModal} className="flex-1 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors">Done</button>
                  <button
                    type="button"
                    onClick={() => navigate('/appearance')}
                    className="flex-1 py-3 rounded-xl text-white text-sm font-bold hover:brightness-110 transition-all shadow-sm"
                    style={{ backgroundColor: THEME_COLOR }}
                  >
                    Open appearance
                  </button>
                </div>
              </div>
            ) : verificationCompleteDomain && snippet ? (
              <div className="space-y-5">
                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">Verification complete</p>
                  <h3 className="mt-2 text-lg font-extrabold tracking-tight text-zinc-900 break-all">{verificationCompleteDomain}</h3>
                  <p className="mt-2 text-sm font-medium leading-6 text-emerald-900/80">
                    Keep your install tag in place. You can also use this canonical install tag for future updates.
                  </p>
                </div>

                <div className="rounded-2xl border border-zinc-200 bg-zinc-950 p-4 text-left">
                  <p className="mb-3 text-[11px] font-black uppercase tracking-[0.16em] text-zinc-400">Install script</p>
                  <code className="block whitespace-pre-wrap break-all text-xs leading-5 text-zinc-100">{snippet}</code>
                </div>

                <button
                  type="button"
                  onClick={() => copyToClipboard(snippet)}
                  className={cn(
                    'w-full rounded-xl border py-3 text-sm font-bold transition-colors',
                    copiedSnippet
                      ? 'border-emerald-200 bg-emerald-100 text-emerald-700'
                      : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
                  )}
                >
                  {copiedSnippet ? 'Install tag copied' : 'Copy install tag'}
                </button>

                {copyError && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-xs font-medium text-amber-700">
                    {copyError}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={resetAddSiteModal} className="flex-1 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors">Done</button>
                  <button
                    type="button"
                    onClick={() => navigate('/appearance')}
                    className="flex-1 py-3 rounded-xl text-white text-sm font-bold hover:brightness-110 transition-all shadow-sm"
                    style={{ backgroundColor: THEME_COLOR }}
                  >
                    Open appearance
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleLink} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 px-1">Store Domain</label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={domain}
                    onChange={e => setDomain(e.target.value)}
                    placeholder="example.com"
                    className="w-full px-4 py-3 rounded-xl border border-zinc-200 text-sm font-medium bg-zinc-50 focus:bg-white focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': THEME_COLOR } as any}
                  />
                  <p className="text-[11px] text-zinc-400 mt-2 px-1">Standard sites may require verification outside local dev. Shopify and WordPress normally open ready from their admin CTA.</p>
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-xs font-medium text-red-600 animate-in shake">
                    {error}
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={resetAddSiteModal} className="flex-1 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors">Cancel</button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-3 rounded-xl text-white text-sm font-bold hover:brightness-110 disabled:opacity-50 transition-all shadow-sm"
                    style={{ backgroundColor: THEME_COLOR }}
                  >
                    {loading ? 'Linking…' : 'Link store'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {legalConsentOpen && (
        <div className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center z-[60] p-6 animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] border border-zinc-200 shadow-2xl w-full max-w-lg p-8 animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-700">Legal agreement</p>
                <h2 className="mt-2 text-xl font-bold text-zinc-900 tracking-tight">Review and agree before installing</h2>
                <p className="text-sm text-zinc-500 font-medium mt-2 leading-6">
                  Standard-site install tags can be generated after you agree to the current Reach legal terms for your account.
                </p>
              </div>
              <button
                onClick={() => {
                  setLegalConsentOpen(false);
                  setLegalConsentError('');
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
              <p className="text-sm font-bold text-zinc-900">Documents</p>
              <div className="mt-3 grid gap-2 text-sm font-semibold text-cyan-800">
                <a href={`${legalSiteUrl}/legal/terms`} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-950">Terms of Service</a>
                <a href={`${legalSiteUrl}/legal/privacy`} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-950">Privacy Policy</a>
                <a href={`${legalSiteUrl}/legal/dpa`} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-950">Data Processing Addendum</a>
                <a href={`${legalSiteUrl}/legal/pipeda`} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-950">PIPEDA Notice</a>
              </div>
            </div>

            <p className="mt-5 text-sm font-medium leading-6 text-zinc-600">
              By continuing, you confirm that you are authorized to accept these terms for the account and any standard website you connect.
            </p>

            {legalConsentError && (
              <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">
                {legalConsentError}
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setLegalConsentOpen(false);
                  setLegalConsentError('');
                }}
                className="flex-1 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLegalConsentAgree}
                disabled={legalConsentSubmitting}
                className="flex-1 py-3 rounded-xl text-white text-sm font-bold hover:brightness-110 disabled:opacity-50 transition-all shadow-sm"
                style={{ backgroundColor: THEME_COLOR }}
              >
                {legalConsentSubmitting ? 'Recording...' : 'Agree and continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {storeToRemove && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-6 animate-in fade-in duration-200">
          <div className="bg-white rounded-[2rem] border border-zinc-200 shadow-2xl w-full max-w-lg p-8 animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-red-600">Store management</p>
                <h2 className="mt-2 text-xl font-bold text-zinc-900 tracking-tight">Remove {storeToRemove.externalDomain}</h2>
                <p className="text-sm text-zinc-500 font-medium mt-2 leading-6">
                  Choose whether to remove this store from your console only, or permanently delete Reach-held data for this store.
                </p>
              </div>
              <button onClick={() => setStoreToRemove(null)} className="w-8 h-8 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setRemovalMode('unlink')}
                className={cn(
                  'w-full rounded-2xl border p-4 text-left transition-colors',
                  removalMode === 'unlink' ? 'border-cyan-200 bg-cyan-50' : 'border-zinc-200 bg-white hover:bg-zinc-50',
                )}
              >
                <p className="text-sm font-extrabold text-zinc-900">Remove from my console</p>
                <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">Unlinks your account from the store. Store data remains available to other linked owners.</p>
              </button>
              <button
                type="button"
                onClick={() => setRemovalMode('delete')}
                className={cn(
                  'w-full rounded-2xl border p-4 text-left transition-colors',
                  removalMode === 'delete' ? 'border-red-200 bg-red-50' : 'border-zinc-200 bg-white hover:bg-zinc-50',
                )}
              >
                <p className="text-sm font-extrabold text-red-700">Delete Reach data for this store</p>
                <p className="mt-1 text-xs font-medium leading-5 text-red-700/80">Deletes widget settings, knowledge, vectors, analytics, conversations, verification records, and store ownership links.</p>
              </button>
            </div>

            {removalMode === 'delete' && (
              <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 p-4">
                <label className="block text-xs font-black uppercase tracking-[0.16em] text-red-700">
                  Type {storeToRemove.externalDomain} to confirm
                </label>
                <input
                  value={deleteConfirmDomain}
                  onChange={(event) => setDeleteConfirmDomain(event.target.value)}
                  className="mt-3 w-full rounded-xl border border-red-200 bg-white px-4 py-3 text-sm font-medium text-zinc-900 focus:outline-none focus:ring-2 focus:ring-red-400"
                  placeholder={storeToRemove.externalDomain}
                />
                <p className="mt-2 text-xs font-medium leading-5 text-red-700/80">
                  This does not cancel an external Shopify, WordPress, Stripe, or Lemon Squeezy subscription by itself.
                </p>
              </div>
            )}

            {removeError && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-100 text-xs font-medium text-red-600">
                {removeError}
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setStoreToRemove(null)} className="flex-1 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600 hover:bg-zinc-50 transition-colors">Cancel</button>
              <button
                type="button"
                onClick={handleRemoveStore}
                disabled={removing || (removalMode === 'delete' && deleteConfirmDomain.trim().toLowerCase() !== storeToRemove.externalDomain.toLowerCase())}
                className={cn(
                  'flex-1 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all shadow-sm',
                  removalMode === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#0f3d5e] hover:bg-[#124a66]',
                )}
              >
                {removing ? 'Removing...' : removalMode === 'delete' ? 'Delete data' : 'Remove store'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
